/**
 * ProfileImageSelectorDialog Component
 * Allows selecting an existing image from the user's file manager
 */

import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material'
import { Search, Close, CheckCircle } from '@mui/icons-material'
import { useUserFiles } from '../../hooks/useUserFiles'
import type { UserFileDto } from '@api/types/files'

interface ProfileImageSelectorDialogProps {
  open: boolean
  onClose: () => void
  onSelect: (file: UserFileDto) => void
  title?: string
  description?: string
}

export function ProfileImageSelectorDialog({
  open,
  onClose,
  onSelect,
  title = 'Select Image',
  description = 'Choose an existing image from your uploaded files',
}: ProfileImageSelectorDialogProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedFile, setSelectedFile] = useState<UserFileDto | null>(null)

  // Fetch images from user's file manager
  const { data, isLoading, error } = useUserFiles({
    mimeTypePrefix: 'image/',
    pageSize: 50,
    searchTerm: searchTerm || undefined,
  })

  const images = data?.files || []

  const handleSelect = () => {
    if (selectedFile) {
      onSelect(selectedFile)
      handleClose()
    }
  }

  const handleClose = () => {
    setSelectedFile(null)
    setSearchTerm('')
    onClose()
  }

  const handleImageClick = (file: UserFileDto) => {
    setSelectedFile(file.id === selectedFile?.id ? null : file)
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '80vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {title}
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>

        {/* Search */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search images..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />

        {/* Loading state */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error state */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load images. Please try again.
          </Alert>
        )}

        {/* Empty state */}
        {!isLoading && !error && images.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {searchTerm ? 'No images found matching your search.' : 'No images uploaded yet.'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Upload images using the Media Manager to select from here.
            </Typography>
          </Box>
        )}

        {/* Image grid */}
        {!isLoading && images.length > 0 && (
          <Grid container spacing={2}>
            {images.map((file) => {
              const isSelected = selectedFile?.id === file.id
              const displayName = file.displayName || file.fileName

              return (
                <Grid item xs={6} sm={4} md={3} key={file.id}>
                  <Box
                    onClick={() => handleImageClick(file)}
                    sx={{
                      position: 'relative',
                      aspectRatio: '4/3',
                      borderRadius: 1,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: 2,
                      borderColor: isSelected ? 'primary.main' : 'transparent',
                      boxShadow: isSelected ? 3 : 0,
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: isSelected ? 'primary.main' : 'grey.400',
                        boxShadow: 1,
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={file.thumbnailUrl || file.fileUrl}
                      alt={displayName}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />

                    {/* Selected indicator */}
                    {isSelected && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          bgcolor: 'rgba(25, 118, 210, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CheckCircle sx={{ fontSize: 48, color: 'primary.main' }} />
                      </Box>
                    )}

                    {/* Dimensions badge */}
                    {file.width && file.height && (
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 4,
                          right: 4,
                          bgcolor: 'rgba(0,0,0,0.6)',
                          color: 'white',
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 0.5,
                          fontSize: '0.65rem',
                        }}
                      >
                        {file.width}x{file.height}
                      </Box>
                    )}
                  </Box>

                  {/* File name */}
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      display: 'block',
                      mt: 0.5,
                      textAlign: 'center',
                      color: isSelected ? 'primary.main' : 'text.secondary',
                      fontWeight: isSelected ? 600 : 400,
                    }}
                  >
                    {displayName}
                  </Typography>
                </Grid>
              )
            })}
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSelect}
          variant="contained"
          disabled={!selectedFile}
        >
          Select Image
        </Button>
      </DialogActions>
    </Dialog>
  )
}
